import { Injectable, inject } from '@angular/core';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AuthenticationDataService } from '@omni/data-services/authentication/authentication.service';
import { LocalizationService } from '@omni/services/localization/localization.service';

/** Dynamics LCID → BCP-47 tag for the app's supported languages (LocalizationService set) —
 *  `userLanguage` rides as the agent's `Accept-Language`. */
const LCID_TO_BCP47: Record<string, string> = {
  '1033': 'en', '1031': 'de', '1036': 'fr', '1041': 'ja', '3082': 'es',
  '1040': 'it', '2070': 'pt', '1055': 'tr', '1043': 'nl', '2052': 'zh-CN',
};

/**
 * The signed-in rep's session/identity context, attached by the transport to every agent call
 * as Foundry `structured_inputs` (top-level keys). The hosted agent's MCP templates these into
 * request headers — systemUserId / environmentUrl / accessToken for impersonation,
 * userPositionIds for territory scoping — plus the reply locale (userTimeZone / userLanguage).
 * Session-constant: identical for every call while the rep is signed in.
 */
export interface UserContext {
  /** Index signature so the bag is a valid `structured_inputs` Record (transport contract). */
  [key: string]: unknown;
  /** The rep's active Dataverse org URL — the MCP's query target. */
  environmentUrl: string;
  /** The rep's Dataverse systemuserid — the MCP impersonates this user. */
  systemUserId: string;
  /** IANA zone (e.g. "Europe/Berlin") — time-relative sources need the rep's zone. */
  userTimeZone: string;
  /** The app language the rep has selected, as a BCP-47 tag (e.g. "de"); the reply language. */
  userLanguage: string;
  /** Owned position id(s), for territory scoping (serialized into the x-rep-positions header). */
  userPositionIds: string[];
  /** The rep's Dataverse bearer for `environmentUrl`; the MCP queries AS the rep. '' ⇒ the MCP
   *  falls back to managed identity + `systemUserId` impersonation. */
  accessToken: string;
}

/**
 * Single assembler for the rep's session/identity context, called by the transport (the one
 * choke point every agent call flows through) so the context is uniform by construction.
 * Identity fields come from AuthenticationService (null-safe so a partially-loaded user never
 * throws); `accessToken` is the rep's Dataverse bearer for the active instance, acquired via
 * AuthenticationDataService (MSAL-backed, cached + auto-refreshed).
 */
@Injectable({ providedIn: 'root' })
export class RepContextService {
  readonly #authService = inject(AuthenticationService);
  readonly #authData = inject(AuthenticationDataService);
  readonly #localization = inject(LocalizationService);

  async buildUserContext(): Promise<UserContext> {
    const user = this.#authService.user;
    // Territory scoping: send the rep's OWN positions only — the agent's MCP derives child
    // territory itself (parentpositionid in {…}) via the in-user-child-positions operator.
    const positionIds = (user?.positions ?? []).map(p => p.ID).filter((id): id is string => !!id);
    // The rep's Dataverse bearer for the active org instance; '' tells the MCP to use managed
    // identity + impersonation.
    const accessToken = await this.#acquireInstanceToken();
    return {
      environmentUrl: this.#authService.userConfig?.activeInstance?.url ?? '',
      systemUserId: user?.systemUserID ?? '',
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userLanguage: this.#replyLanguage(user?.localeId),
      userPositionIds: [...new Set(positionIds)],
      accessToken,
    };
  }

  /**
   * The app language the rep has selected, read on every call so a language change applies to
   * the next turn. Falls back to the Dynamics user's LCID, which updates only after a sync, and
   * then to English.
   */
  #replyLanguage(userLocaleId: string | undefined): string {
    return LCID_TO_BCP47[this.#localization.selectedLanguage?.localeID ?? '']
      ?? LCID_TO_BCP47[userLocaleId ?? '']
      ?? 'en';
  }

  /** Best-effort: a token failure must not break an agent call — the MCP falls back to
   *  managed identity + impersonation when `accessToken` is ''. */
  async #acquireInstanceToken(): Promise<string> {
    try {
      const token = await this.#authData.getTokenForSelectedInstance();
      return token?.token ?? '';
    } catch {
      return '';
    }
  }
}
